import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { ResourceService } from './services/resource.service';
import { PopulationService } from './services/population.service';
import { TechnologyService } from './services/technology.service';
import { Farm } from './production/farm/farm';
import { Forester } from './production/forester/forester';
import * as Constants from './globals/constants';
import { House } from './production/house/house';
import { Subject } from 'rxjs';
import { Masonry } from './production/masonry/masonry';
import { Foundry } from './production/foundry/foundry';
import { GoldSmith } from './production/goldsmith/gold-smith';
import { Powerplant } from './production/powerplant/powerplant';
import { Datacenter } from './production/datacenter/datacenter';
import { Helper } from './utilities/helper';
import { AscensionService } from './services/ascension.service';

declare const window;

@Component({
  selector: 'app-game',
  templateUrl: './game.component.html',
  styleUrls: ['./game.component.scss']
})
export class GameComponent implements OnInit {

  public initialised = false;
  private continueLoop: boolean;
  private previousTimestamp;
  private valueUpdatesPerSecond = 10;
  public count;
  private buildingList;

  public resourceService;
  public populationService;
  public technologyService;
  public ascensionService;
  public autoSaveLoop;
  private previousPlayTimeMeasurement = Date.now();
  public playtime = 0;
  public exportedState;

  private isPaused = false;

  public tick: Subject<any>;
  private helper = new Helper();

  public menuActive = false;

  public Game = this;

  @ViewChild('importsave', {static: false})
  public importSave: any;

  constructor(private ngZone: NgZone, resourceService: ResourceService, populationService: PopulationService, technologyService: TechnologyService, ascensionService: AscensionService) {
    Object.keys(Constants.OWNED_BUILDINGS).forEach((key) => {
      delete Constants.OWNED_BUILDINGS[key];
    });
    this.resourceService = resourceService;
    this.populationService = populationService;
    this.technologyService = technologyService;
    this.ascensionService = ascensionService;

    this.tick = new Subject();
    this.buildingList = {
      house: new House(this.resourceService, this.populationService, this.technologyService),
      farm: new Farm(this.resourceService, this.populationService, this.technologyService),
      forester: new Forester(this.resourceService, this.populationService, this.technologyService),
      masonry: new Masonry(this.resourceService, this.populationService, this.technologyService),
      foundry: new Foundry(this.resourceService, this.populationService, this.technologyService),
      goldsmith: new GoldSmith(this.resourceService, this.populationService, this.technologyService),
      powerplant: new Powerplant(this.resourceService, this.populationService, this.technologyService),
      datacenter: new Datacenter(this.resourceService, this.populationService, this.technologyService),
    };
    window.Game = this;
  }

  ngOnInit() {
    this.menuActive = true;
  }

  public start() {
    if (this.continueLoop) {
      return;
    }
    this.previousTimestamp = undefined;
    this.continueLoop = true;
    window.requestAnimationFrame(this.loop);
    this.autoSaveLoop = setInterval(this.autoSave, 10000);
  }

  public pause() {
    this.previousTimestamp = undefined;
    this.continueLoop = false;
    clearInterval(this.autoSaveLoop);
  }

  public autoSave = () => {
    this.save('GAME.AUTOSAVE');
  }

  public loadAutoSave = () => {
    this.load('GAME.AUTOSAVE');
  }

  public calculateTimePassed() {
    const now = Date.now();
    this.playtime += now - this.previousPlayTimeMeasurement;
    this.previousPlayTimeMeasurement = now;
  }

  public idle() {
    this.continueLoop = false;
  }

  private loop = (timestamp) => {
    if (!this.previousTimestamp) { this.previousTimestamp = timestamp; }
    const timePassed = timestamp - this.previousTimestamp;

    if (timePassed > (1000 / this.valueUpdatesPerSecond)) {
      this.previousTimestamp = timestamp;

      this.doTick(timePassed);
    }

    if (this.continueLoop) {
      window.requestAnimationFrame(this.loop);
    }
  }

  public doTick(timePassed: number) {
    if (this.ascensionService.state.unlocked.time_distortion) {
      timePassed *= (1 + (.25 * this.ascensionService.state.times_ascended));
    }

    Object.values(this.buildingList).forEach((building: any) => {
      building.tick(timePassed);
    });
    this.tick.next();
  }

  public explore(amount) {
    const price = this.explorationCosts();
    for (let i = 0; i < amount; i++) {
      if (this.resourceService.payResource(price)) {
        this.resourceService.addResource('acres', 1);
        this.resourceService.addResource('freeAcres', 1);
      }
    }
  }

  public explorationCosts() {
    const price = {};
    Object.entries(Constants.SETTINGS.EXPLORATION.COSTS).forEach((entry) => {
      const baseCost = entry[1] + (Constants.SETTINGS.EXPLORATION.COST_MULTIPLIER
        * (this.resourceService.getResource('acres') - this.resourceService.getResource('baseAcres') ) );

      let multiplier = 1;
      if (this.technologyService.state.wheel) {
        multiplier *= .95; // -5%
      }
      if (this.technologyService.state.paths) {
        multiplier *= .95; // -5%
      }
      if (this.technologyService.state.stone_roads) {
        multiplier *= .9; // -10%
      }
      if (this.technologyService.state.cartography) {
        multiplier *= .95; // -5%
      }
      if (this.technologyService.state.exploration) {
        multiplier *= .95; // -5%
      }
      if (this.technologyService.state.railroads) {
        multiplier *= .95; // -5%
      }
      if (this.technologyService.state.flight) {
        multiplier *= .9; // -10%
      }
      if (this.technologyService.state.space_flight) {
        multiplier *= .95; // -10%
      }
      if (this.technologyService.state.deep_space_travel) {
        multiplier *= .85; // -15%
      }
      if (this.technologyService.state.star_gate) {
        multiplier *= .9; // -10%
      }

      price[entry[0]] = Math.round(baseCost * multiplier);
    });
    return price;
  }

  public efficiency() {
    return Math.round(this.populationService.calculateEfficiency() * 100);
  }

  public powerEfficiency() {
    return Math.round(this.resourceService.calculatePowerEfficiency() * 100);
  }

  public research(tech) {
    this.technologyService.research(tech);
  }

  public save(saveName = 'saveData') {
    if (saveName === 'saveData') {
      this.closeMenu();
    }
    localStorage.setItem(saveName, JSON.stringify(this.gameState));
  }

  public get gameState() {
    const gameState: any = {};
    this.calculateTimePassed();
    gameState.saveDate = Date.now();
    gameState.playtime = this.playtime;
    gameState.technology = this.technologyService.state;
    gameState.ascension = this.ascensionService.state;
    gameState.resources = this.resourceService.resources;
    gameState.buildings = {
      house: this.buildingList.house.numberOfBuildings,
      farm: this.buildingList.farm.numberOfBuildings,
      forester: this.buildingList.forester.numberOfBuildings,
      masonry: this.buildingList.masonry.numberOfBuildings,
      foundry: this.buildingList.foundry.numberOfBuildings,
      goldsmith: this.buildingList.goldsmith.numberOfBuildings,
      powerplant: this.buildingList.powerplant.numberOfBuildings,
      datacenter: this.buildingList.datacenter.numberOfBuildings,
    };
    return gameState;
  }

  public set gameState(gameState) {
    this.initialised = true;
    if (gameState.playtime) {
      this.playtime = gameState.playtime;
    } else {
      this.playtime = 0;
    }

    this.technologyService.STATE = this.helper.deepMergeObject({} , gameState.technology);
    this.ascensionService.STATE = this.helper.deepMergeObject(Constants.ASCENSION_STATE, gameState.ascension);
    Constants.SINGLETON_TECH.STATE = this.technologyService.STATE;
    Constants.SINGLETON_ASCENSION.STATE = this.ascensionService.STATE;
    this.technologyService.triggerTechUpdate();
    this.resourceService.resources = gameState.resources;
    this.populationService.availableJobs = 0;
    this.buildingList.house.loadBuilding(gameState.buildings.house);
    this.buildingList.farm.loadBuilding(gameState.buildings.farm);
    this.buildingList.forester.loadBuilding(gameState.buildings.forester);
    this.buildingList.masonry.loadBuilding(gameState.buildings.masonry);
    this.buildingList.foundry.loadBuilding(gameState.buildings.foundry);
    this.buildingList.goldsmith.loadBuilding(gameState.buildings.goldsmith);
    this.buildingList.powerplant.loadBuilding(gameState.buildings.powerplant);
    this.buildingList.datacenter.loadBuilding(gameState.buildings.datacenter);
    if (this.ascensionService.state.unlocked.thoughtless_thoughts) {
      this.offlineTick(gameState.saveDate);
    }
  }

  public load(saveName = 'saveData') {
    this.menuActive = false;
    const saveFile = JSON.parse(localStorage.getItem(saveName));
    this.gameState = saveFile;
  }

  public offlineTick(prevTime) {
    let timeDiff = Date.now() - prevTime;

    const maxEfficientOfflineTime = 28800000; // 8 hours of max efficiency
    if (timeDiff > maxEfficientOfflineTime) {
      let remainder = timeDiff - maxEfficientOfflineTime;
      timeDiff = timeDiff - maxEfficientOfflineTime;
      remainder = remainder * 0.1; // remainder time is only 10% effective.
      timeDiff += remainder;
    }
    if (this.ascensionService.state.unlocked.creating_thoughts) {
      timeDiff = timeDiff * 0.35;
    } else if (this.ascensionService.state.unlocked.evaluating_thoughts) {
      timeDiff = timeDiff * 0.30;
    } else if (this.ascensionService.state.unlocked.analysing_thoughts) {
      timeDiff = timeDiff * 0.25;
    } else if (this.ascensionService.state.unlocked.applying_thoughts) {
      timeDiff = timeDiff * 0.20;
    } else if (this.ascensionService.state.unlocked.understanding_thoughts) {
      timeDiff = timeDiff * 0.15;
    } else if (this.ascensionService.state.unlocked.remembering_thoughts) {
      timeDiff = timeDiff * 0.10;
    } else if (this.ascensionService.state.unlocked.thoughtless_thoughts) {
      timeDiff = timeDiff * 0.05;
    }
    this.doTick(timeDiff);
  }

  public togglePause() {
    if (this.isPaused) {
      this.isPaused = false;
      this.start();
    } else {
      this.isPaused = true;
      this.pause();
    }
  }

  public reset(hardReset = false) {
    return new Promise((resolve) => {
      this.pause();

      setTimeout(() => {
        this.technologyService.reset(hardReset);
        this.ascensionService.reset(hardReset);
        this.populationService.reset();
        this.resourceService.reset();
        Object.keys(Constants.OWNED_BUILDINGS).forEach((key) => {
          delete Constants.OWNED_BUILDINGS[key];
        });
        Object.values(this.buildingList).forEach((building: any) => {
          building.reset();
        });
        this.start();
        this.initialised = true;
        this.menuActive = false;
        resolve();
      }, 100);
    });
  }

  private setAscensionResources() {
    if (this.ascensionService.state.unlocked.berry_bush) {
      this.resourceService.addResource('food', 20000);
    }
    if (this.ascensionService.state.unlocked.firewood) {
      this.resourceService.addResource('wood', 2000);
    }
    if (this.ascensionService.state.unlocked.stone_pile) {
      this.resourceService.addResource('stone', 500);
    }
  }

  public export() {
    this.exportedState = btoa(JSON.stringify(this.gameState));
  }

  public import() {
     if (this.importSave.nativeElement.value) {
      try {
        this.gameState = JSON.parse(atob(this.importSave.nativeElement.value));
        this.closeMenu();
      } catch (e) {
        console.log('Import error, invalid import string');
      }
    }
  }

  public ascend() {
    setTimeout(() => {
      this.reset();
    }, 2000)
    this.ascensionService.ascend(this.Game);
    this.pause();
  }

  public toggleMenu() {
    if (this.menuActive) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  public openMenu() {
    this.menuActive = true;
    this.pause();
  }

  public closeMenu() {
    this.menuActive = false;
    this.start();
  }

  public cheat() {
    this.resourceService.RESOURCES.food = 10000000;
    this.resourceService.RESOURCES.wood = 10000000;
    this.resourceService.RESOURCES.stone = 10000000;
    this.resourceService.RESOURCES.metal = 10000000;
    this.resourceService.RESOURCES.gold = 10000000;
    this.resourceService.RESOURCES.data = 10000000;
    this.resourceService.RESOURCES.acres = 10000000;
  }
}
